import { OrganizationWorkspaceItemDto } from '@api/Api';
import ImageComponent from '@components/ImageComponent';
import { unarchiveWorkspace } from '@api/Workspace';
import useTypedDispatch from '@hooks/useTypedDispatch';
import {
  setOrganizationArchivedWorkspacesListNeedRefresh,
  setOrganizationWorkspacesListNeedRefresh
} from '@redux/reducers/organizations';
import { setWorkspaces } from '@redux/actions/workspaceAction';
import { useOrganization } from '@components/OrganizationBoundary';
import { nonNull } from '@helpers/non-null';
import { Button, message } from 'antd';
import React, { memo, useCallback, useState } from 'react';

type ArchivedWorkspaceItemProps = {
  workspace: OrganizationWorkspaceItemDto;
};

export default memo(function ArchivedWorkspaceItem({
  workspace
}: ArchivedWorkspaceItemProps) {
  const dispatch = useTypedDispatch();
  const { currentOrganization } = useOrganization(true);
  const organization = nonNull(currentOrganization.entity);
  const organizationId = organization.id;
  const [loading, setLoading] = useState(false);

  const archivedDate = workspace.archivedAt
    ? new Date(workspace.archivedAt).toLocaleDateString()
    : null;

  const onUnarchive = useCallback(async () => {
    setLoading(true);
    try {
      await unarchiveWorkspace({ workspaceId: workspace.id });
      dispatch(
        setOrganizationArchivedWorkspacesListNeedRefresh({ organizationId })
      );
      dispatch(setOrganizationWorkspacesListNeedRefresh({ organizationId }));
      dispatch(setWorkspaces());
    } catch {
      message.error('Failed to unarchive workspace');
    } finally {
      setLoading(false);
    }
  }, [dispatch, workspace.id, organizationId]);

  return (
    <div className="workspace_card_container">
      <div className="card">
        <div className="row">
          <div className="workspace_info">
            <div className="workspace_info__image">
              <ImageComponent
                alt={`${workspace.name} avatar`}
                src={workspace.picture.url}
                width={40}
                height={40}
                types={['png', 'webp']}
                id={workspace.id}
                name={workspace.name}
              />
            </div>
            <div className="workspace_info__copy">
              <div className="name">{workspace.name}</div>
              <div className="users_count">
                {workspace.totalMembersCount}{' '}
                {workspace.totalMembersCount > 1 ? 'users' : 'user'}
                {archivedDate && <span> &middot; Archived {archivedDate}</span>}
              </div>
            </div>
          </div>
          <div className="workspace_action">
            {workspace.permissions.delete && (
              <Button type="link" loading={loading} onClick={onUnarchive}>
                Unarchive
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
